import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@resource-kit/ui/react';

interface PasswordFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange'
> {
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  labelRight?: React.ReactNode;
  error?: string | null;
}

const EyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const EyeOffIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-5 h-5'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
    />
  </svg>
);

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  name,
  label,
  labelRight,
  value,
  onChange,
  disabled,
  required,
  autoComplete,
  error,
  ...rest
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <label htmlFor={id || name} className="block text-sm font-medium text-foreground">
          {label} {required ? '*' : ''}
        </label>
        {labelRight}
      </div>
      <div className="relative">
        <Input
          id={id || name}
          name={name}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? `${id || name}-error` : undefined}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={show ? t('password.hide') : t('password.show')}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {error && (
        <p id={`${id || name}-error`} className="text-xs text-destructive mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

export default PasswordField;
